import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';

const TOPICS = [
  ['billing', 'Billing'],
  ['account', 'Account access'],
  ['technical', 'Something broken'],
  ['sales', 'Buying'],
  ['product', 'How the product works'],
];

function when(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export default function SupportQueue() {
  const [params] = useSearchParams();
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [routes, setRoutes] = useState({});
  const [fallback, setFallback] = useState('');
  const [replyToVisitor, setReplyToVisitor] = useState(true);
  const [selected, setSelected] = useState(params.get('ticket') || '');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    const data = await apiGet('/support/queue');
    setQueue(data.tickets || []);
    setRoutes(data.routes || {});
    setFallback(data.fallback || '');
    setReplyToVisitor(data.replyToVisitor !== false);
    setError('');
  };

  useEffect(() => {
    if (!isBackendConfigured()) { setError('The API is not configured.'); return; }
    load().catch((err) => setError(err.message || 'Could not load the desk.'));
  }, []);

  const ticket = (queue || []).find((t) => t.id === selected) || null;

  const pick = (t) => {
    setSelected(t.id);
    setReply(t.replyText || t.draftText || t.answer || '');
  };

  useEffect(() => {
    if (!queue || !selected) return;
    const t = queue.find((x) => x.id === selected);
    if (t) setReply(t.replyText || t.draftText || t.answer || '');
  }, [queue, selected]);

  const saveRoutes = async (e) => {
    e.preventDefault();
    setBusy('routes');
    setNotice('');
    try {
      await apiPost('/support/routes', { routes, fallback, replyToVisitor });
      setNotice('Routing saved. New requests use it within a minute.');
    } catch (err) {
      setError(err.message || 'Could not save routing.');
    } finally {
      setBusy('');
    }
  };

  const sendReply = async () => {
    if (!ticket) return;
    setBusy('reply');
    setNotice('');
    try {
      await apiPost(`/support/tickets/${ticket.id}/reply`, { text: reply });
      setNotice('Reply queued.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not send that reply.');
    } finally {
      setBusy('');
    }
  };

  const close = async () => {
    if (!ticket) return;
    setBusy('close');
    try {
      await apiPost(`/support/tickets/${ticket.id}/close`, {});
      await load();
    } catch (err) {
      setError(err.message || 'Could not close that.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="cs-store">
      <header className="cs-store-hero">
        <h1>Support desk</h1>
        <p>Requests from the site and from signed-in accounts. Each one is drafted, then mailed to the teammate for that topic.</p>
      </header>
      <section className="cs-store-section cs-support-desk">
        {error && <p className="cs-land-micro">{error}</p>}
        <form className="cs-sw-card" onSubmit={saveRoutes}>
          <h3>Who is notified</h3>
          <p className="cs-help">Leave a topic blank to use the fallback. A blank fallback uses the sales inbox.</p>
          {TOPICS.map(([id, label]) => (
            <div className="cs-field" key={id}>
              <label>{label}</label>
              <input className="cs-input" type="email" value={routes[id] || ''} placeholder="name@nventr.ai" onChange={(e) => setRoutes((r) => ({ ...r, [id]: e.target.value }))} />
            </div>
          ))}
          <div className="cs-field">
            <label>Fallback</label>
            <input className="cs-input" type="email" value={fallback} placeholder="sales inbox" onChange={(e) => setFallback(e.target.value)} />
          </div>
          <label className="cs-help" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={replyToVisitor} onChange={(e) => setReplyToVisitor(e.target.checked)} />
            Email the cited answer to the person who wrote in
          </label>
          <button className="cs-btn cs-btn-primary" type="submit" disabled={busy === 'routes'} style={{ marginTop: 12 }}>
            {busy === 'routes' ? 'Saving…' : 'Save routing'}
          </button>
          {notice && <p className="cs-help">{notice}</p>}
        </form>

        <div className="cs-support-split">
          <div className="cs-sw-card">
            <h3>Queue</h3>
            {!queue && <p className="cs-help">Loading…</p>}
            {queue && queue.length === 0 && <p className="cs-help">No requests yet.</p>}
            <ul className="cs-support-list">
              {(queue || []).map((t) => (
                <li key={t.id}>
                  <button type="button" className={`cs-support-row${t.id === selected ? ' is-on' : ''}`} onClick={() => pick(t)}>
                    <strong>{t.subject || t.message?.slice(0, 80) || 'Request'}</strong>
                    <span className="cs-help">{t.topicLabel || 'New'} · {t.audience === 'member' ? 'Signed in' : 'Public'} · {t.status}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="cs-sw-card">
            {!ticket && <p className="cs-help">Select a request. <Link to="/support">Open the public form</Link></p>}
            {ticket && (
              <>
                <h3>{ticket.subject || 'Request'}</h3>
                <p className="cs-help">
                  {ticket.name} &lt;{ticket.email}&gt; · {ticket.audience === 'member' ? 'Signed in' : 'Public'}
                  {ticket.orgName ? ` · ${ticket.orgName}` : ''} · {when(ticket.createdAt)}
                </p>
                <p className="cs-help">Notified: {ticket.assigneeEmail || 'nobody yet'}{ticket.routeRule ? ` (${ticket.routeRule})` : ''}</p>
                <p style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</p>
                <div className="cs-field">
                  <label>Reply</label>
                  <textarea className="cs-input" rows={8} value={reply} onChange={(e) => setReply(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="cs-btn cs-btn-primary" type="button" disabled={busy === 'reply'} onClick={sendReply}>
                    {busy === 'reply' ? 'Sending…' : 'Email this reply'}
                  </button>
                  {ticket.status !== 'closed' && (
                    <button className="cs-btn cs-btn-outline" type="button" disabled={busy === 'close'} onClick={close}>
                      Close
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
